// Single source of truth for the application run mode.
//
// REACT_APP_MODE ∈ { "production", "preview", "demo" } (default "production").
// - production : real APIs, Electron / FastAPI backend, real tenant data.
// - preview    : reserved for staging. At the DATA layer it currently behaves
//                exactly like production (real APIs); kept distinct so future
//                staging behavior can branch here without a redesign.
// - demo       : the SAME frontend, but every API request is served by the
//                in-browser mock data provider (services/demo). No backend,
//                no tunnel, no real data. Used for demo.jewel-flow.com.
//
// This value is inlined at build time by CRA/webpack from REACT_APP_MODE, so
// when the mode is not "demo" the demo branches are dead code the bundler can
// drop. A defensive hostname fallback only ever *adds* demo mode when the page
// is actually served from a `demo.` host — it never disables it.

const RAW_MODE = String(process.env.REACT_APP_MODE || "").trim().toLowerCase();

const VALID_MODES = new Set(["production", "preview", "demo"]);

function hostnameLooksLikeDemo() {
  try {
    if (typeof window === "undefined") return false;
    const host = String(window.location.hostname || "").toLowerCase();
    
    // Canonical demo hosts
    if (host === "demo.jewel-flow.com" || host === "jewelflow-demo.pages.dev") return true;
    
    // Pattern matches
    if (host.startsWith("demo.")) return true;
    if (host.includes("jewelflow-demo")) return true;
    
    return false;
  } catch {
    return false;
  }
}

function resolveMode() {
  if (RAW_MODE === "demo") return "demo";
  // Defensive: if a non-demo build is somehow served from a demo host, still
  // run as demo so real APIs are never reached from that surface.
  if (hostnameLooksLikeDemo()) return "demo";
  if (VALID_MODES.has(RAW_MODE)) return RAW_MODE;
  return "production";
}

const MODE = resolveMode();

export function getMode() {
  return MODE;
}

export function isProductionMode() {
  return MODE === "production";
}

export function isPreviewMode() {
  return MODE === "preview";
}

export function isDemoMode() {
  return MODE === "demo";
}
